import { escapeRegExp, isArray, isString } from "lodash";

type stringOrNumber = string | number;
type StringTransformFunction = (value: stringOrNumber, key: string) => string;

const _transforms: Record<string, StringTransformFunction> = {};
const RE_NO_SPACE = /\u200B/g;

/**
 * Creates a case-insensitive regular expression from the input string,
 * with special characters escaped.
 * @param searchText - The input string to create the regular expression from.
 * @returns The case-insensitive regular expression or null if the input is empty.
 */
export function createSearchRegex(searchText: string): RegExp | null {
	return searchText ? new RegExp(escapeRegExp(searchText), "i") : null;
}

/**
 * Return a unique string value for the given string and set of existing values
 */
export function uniqueString(value: string, existingValues: Set<string>, addToExisting = true): string {
	let n = 0;
	let newValue = value;

	while (existingValues.has(newValue)) {
		newValue = `${value}${++n}`; 
	}

	if (addToExisting) {
		existingValues.add(newValue);
	}

	return newValue;
}

/**
 * Register a transform function for string substitution that takes the parameter and returns the string to substitute into the template;
 * transform functions must be uniquely names across the application
 */
export function registerStringSubstituteTransform(name: string, transformFunc: StringTransformFunction): void {
	_transforms[name] = transformFunc;
}

/**
 * Return true if given two strings are the same for search purposes
 */
export function searchStringEquals(searchString: string, value: string): boolean {
	if (!searchString && !value) {
		return true;
	}

	return !!value && searchString?.localeCompare(value, undefined, { sensitivity: "accent" }) === 0;
}

/**
 * Combine base string value with separator and suffix if condition is met
 * @param base - starting string value
 * @param suffix - string to append to base
 * @param separator - separator to insert between base and suffix
 * @param condition - condition for test to add; defaults to testing for non-empty suffix
 */
export function stringCombine(base = "", suffix = "", separator = "", condition = true): string {
	if (!suffix) {
		return base;
	} else if (!base) {
		return suffix;
	}

	return `${base}${condition ? `${separator}${suffix}` : ""}`;
}

/**
 * Fill parameterized string with given parameters, or through registered transforms
 * 
 * For example:
 *		registerStringSubstituteTransform("translate", (value, key) => "bucket");
 * 		stringSubstitute("There are ${0} items in the ${cart:translate}", [5]) === "There are 5 items in the bucket";
 * 		stringSubstitute("There are ${count} ${type} in the cart", {count: 3, type: "items"}) === "There are 3 items in the cart";
 */
export function stringSubstitute(template: string, params?: Record<string, stringOrNumber> | stringOrNumber[]): string {
	return template.replace(/\$\{([^\s\:\}]*)(?:\:([^\s\:\}]+))?\}/g, (match, key, transform) => {
		if (key === "") {
			return "$";
		}

		let value = params?.[key];

		if (transform) {
			value = _transforms[transform](value, key);
		}

		if (typeof value == null) {
			throw new Error(`stringSubstitute could not find parameter "${key}" in template "${template}"`);
		}

		return value.toString();
	});
};

/** Return trimmed string value; if no string given, then return empty string */
export function trimmedString(value: string | undefined): string {
	return value ? value.trim() : "";
}

/** remove the line breaks from string or an array of strings and return the value */
export function stringStripLineBreaks(value: string | string[]): string | string[] {
	if (isArray(value)) {
		for (let i = 0; i < value.length; ++i) {
			value[i] = value[i].replace(/(\r\n|\n|\r|\t)/gm, " ");
		}
	} else {
		value = value.replace(/(\r\n|\n|\r|\t)/gm, " ");
	}
	return value;
}

/** Trim and remove no-space characters inserted to allow text wrapping */
export function purifyText(s: string): string {
	return s.replace(RE_NO_SPACE, "").trim();
}

// define overload signatures to return string or array of strings
export function trimAndPurifyText(value: string, format: string, formatAfterPurify?: boolean): string;
export function trimAndPurifyText(value: string[], format: string, formatAfterPurify?: boolean): string[];

/** trim and purify text based on format */
export function trimAndPurifyText(value: string | string[], format: string, formatAfterPurify?: boolean): string | string[] {
	if (!format) {
		value = stringStripLineBreaks(value);
	}

	if (isString(value)) {
		value = purifyText(value);
	} else {
		// TODO: do we need this? is value ever an array? what does it mean for value to NOT be a string?
		for (const key in value) {
			if (isArray(value[key])) {
				const valueFromArray = value[key][0];
				const purifiedValueFromArray = purifyText(valueFromArray);
				value[key] = formatAfterPurify ? formatStringValue(purifiedValueFromArray) : purifiedValueFromArray;
			} else {
				const purifiedValue = purifyText(value[key]);
				value[key] = formatAfterPurify ? formatStringValue(purifiedValue) : purifiedValue;
			}
		}
	}

	return value;
}

/** format string value */
export function formatStringValue(value: string): string {
	return (value == null) ? "" : String(value);
}